<script setup lang="ts">
  import { ref, computed, watchEffect } from "vue";
  import { useI18n } from "vue-i18n";
  import { IconArrowLeft } from "@ui-kit";
  import { TopBar, StatusBar } from "@src/components";
  import { type Product, type Good, useLazyProductsLoad, useLoadByExternal, useOrderActionList } from "@src/api";
  import { useStateActionList, useOrderItemListState, useOrders } from "./state";
  import { useOrderListStore, useWidgetsStore } from "@src/store";
  import { useProfileOptions } from "@src/hooks";
  import { default as OrderCardSkeleton } from "./OrderCardSkeleton.vue";
  import { default as OrderCardPicking } from "./OrderCardPicking.vue";
  import { default as OrderCardCompleted } from "./OrderCardCompleted.vue";
  import { default as OrderCardCreated } from "./OrderCardCreated.vue";
  import { default as OrderCardCompletedModal } from "./OrderCardCompletedModal.vue";

  const isLoading = ref(true);

  const { execute: getItems } = useLazyProductsLoad();
  const { execute: getListSeria } = useLoadByExternal();
  const { execute: getOrderActionList } = useOrderActionList();

  const orderState = useOrders();
  const orderListStore = useOrderListStore();
  const widgets_store = useWidgetsStore();
  const actionListState = useStateActionList();
  const orderItemListState = useOrderItemListState();
  const { profile } = useProfileOptions();
  const i18n = useI18n();

  const order = computed(() => orderListStore.map[widgets_store.order_card.order_id]);
  const isCompleteModalOpen = ref(false);

  const order_card_title = computed(() => {
    if (!order.value) return "";

    if (order.value.status === "picking" && order.value.executed_by === profile.value.current_user.user_id) {
      return i18n.t("Сборка товара");
    }

    if (order.value.order_type === "pickup.commerce") {
      return i18n.t("Коммерческий запрос");
    }

    return i18n.t("Запрос покупателя");
  });

  const loadOrder = async () => {
    if (!order.value) return;

    // подготавливаем данные и загружаем товары
    const { ids, map } = order.value.goods.reduce<{ ids: string[]; map: Record<string, Good> }>(
      (acc, good) => {
        acc.ids.push(good.item_id);
        acc.map[good.item_id] = good;

        return acc;
      },
      { ids: [], map: {} },
    );
    const itemsResult = await getItems({ item_id: ids });

    // подготавливаем данные и загружаем цветомодели
    if (itemsResult.isSuccess.value) {
      const seriaGuid: string[] = [];
      const serias: Record<string, Product> = {};
      const goods: Record<string, { item: Product; quantity: number; result: number }[]> = {};

      for (const item of itemsResult.data.value.items) {
        const good = { item, quantity: map[item.item_id].quantity ?? 0, result: map[item.item_id].result ?? 0 };

        if (goods[item.seria_guid]) {
          goods[item.seria_guid].push(good);
        } else {
          goods[item.seria_guid] = [good];
          seriaGuid.push(item.seria_guid);
        }
      }

      const seriasResult = await getListSeria({ guid: seriaGuid });

      // подготавливаем и сохраняем всё загруженное
      if (seriasResult.isSuccess.value) {
        for (const item of seriasResult.data.value.items) {
          serias[item.external_id] = item;
        }

        orderState.setSelectedSerias(serias);
        orderState.setSelectedGoods(goods);

        const orderActionListResult = await getOrderActionList({ order_id: order.value.order_id });

        if (orderActionListResult.isSuccess.value) {
          actionListState.set(orderActionListResult.data.value.actions);
        }

        const itemList = Object.entries(orderState.value.selected.serias)
          .map(([seriaId, itemSeria]) => {
            return {
              seriaId,
              seria: itemSeria,
              goods: orderState.value.selected.goodsBySeria[seriaId].map(
                (goodId) => orderState.value.selected.mapGoods[goodId],
              ),
              isCollected:
                order.value?.status !== "request" &&
                orderState.value.selected.goodsBySeria[seriaId].every((goodId) => {
                  const countTotal = actionListState.getCountActions({ itemId: goodId });
                  const countCollected = actionListState.getCountActions({ itemId: goodId, status: "done" });

                  return countCollected === countTotal;
                }),
              isCanceled:
                order.value?.status !== "request" &&
                orderState.value.selected.goodsBySeria[seriaId].some((goodId) => {
                  const countCanceled = actionListState.getCountActions({ itemId: goodId, status: "cancelled" });

                  return countCanceled > 0;
                }),
            };
          })
          .sort((a, b) => Number(a.isCollected) - Number(b.isCollected));

        orderItemListState.set(itemList);
        isLoading.value = false;
      }
    }
  };

  const handleClickToOrders = () => {
    isCompleteModalOpen.value = false;
    widgets_store.close("order_card");
    widgets_store.open("orders");
  };

  const handleClickClose = () => {
    if (widgets_store.order_card.back) {
      widgets_store.open(widgets_store.order_card.back);
    }

    widgets_store.close("order_card");
    isCompleteModalOpen.value = false;
  };

  const handleClickToMain = () => {
    widgets_store.close("order_card");
    widgets_store.close("orders");
  };

  watchEffect(loadOrder);
</script>

<template>
  <div class="order-card-widget" data-testid="order-card">
    <StatusBar />
    <OrderCardCompletedModal
      v-if="isCompleteModalOpen"
      :order_type="order?.order_type"
      @close="handleClickToMain"
      @toOrders="handleClickToOrders"
    />
    <template v-if="!isCompleteModalOpen">
      <TopBar :title="order_card_title" @click:left="handleClickClose">
        <template #left>
          <IconArrowLeft />
        </template>
      </TopBar>
      <OrderCardSkeleton v-if="isLoading || !order" />
      <OrderCardCreated
        v-else-if="
          order.status === 'request' ||
          (order.status === 'picking' && order.executed_by !== profile.current_user.user_id)
        "
      />
      <OrderCardPicking v-else-if="order.status === 'picking'" @complete="isCompleteModalOpen = true" />
      <OrderCardCompleted v-else />
    </template>
  </div>
</template>

<style scoped>
  .order-card-widget {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    gap: 8px;
    overflow-y: hidden;
    flex-direction: column;
    z-index: 1;
    background-color: var(--sf-base-background-white);
  }
</style>
